// src/components/AmmoPage.jsx
import React, { useState, useMemo } from 'react'
import { getAmmoForProfile } from '../data/ammoDB'
import { useAuth } from '../lib/AuthContext'

// ─── Use Case Filter Config ────────────────────────────────────
const USE_CASES = [
  { id: 'all',     label: 'All Rounds' },
  { id: 'hunting', label: 'Hunting'    },
  { id: 'defense', label: 'Defense'    },
  { id: 'range',   label: 'Range'      },
]

// ─── Penetration Bar ──────────────────────────────────────────
function PenetrationBar({ rating, max = 5 }) {
  const pct = (rating / max) * 100
  const color = rating >= 4 ? 'var(--green)' : rating >= 3 ? 'var(--accent)' : 'var(--red)'
  return (
    <div style={styles.barWrap}>
      <div style={styles.barTrack}>
        <div style={{ ...styles.barFill, width: `${pct}%`, background: color }} />
      </div>
      <span style={{ ...styles.barLabel, color }}>{rating}/{max}</span>
    </div>
  )
}

// ─── Twist Warning Badge ──────────────────────────────────────
function TwistWarning({ warning }) {
  if (!warning) return null
  return (
    <div style={styles.twistWarn}>
      <span style={styles.warnIcon}>⚠</span>
      <span style={styles.warnText}>{warning}</span>
    </div>
  )
}

// ─── Terminal Tag ─────────────────────────────────────────────
function TermTag({ label, color }) {
  const colors = {
    blue:   { bg: 'rgba(91,155,213,0.15)',   text: '#5b9bd5',   border: 'rgba(91,155,213,0.25)'   },
    green:  { bg: 'rgba(76,175,125,0.15)',   text: '#4caf7d',   border: 'rgba(76,175,125,0.25)'   },
    gold:   { bg: 'rgba(200,169,110,0.15)',  text: '#e8c98e',   border: 'rgba(200,169,110,0.25)'  },
    red:    { bg: 'rgba(224,92,92,0.12)',    text: '#e05c5c',   border: 'rgba(224,92,92,0.2)'     },
    purple: { bg: 'rgba(150,100,200,0.12)',  text: '#b088d4',   border: 'rgba(150,100,200,0.2)'   },
  }
  const c = colors[color] || colors.blue
  return (
    <span style={{
      ...styles.termTag,
      background: c.bg, color: c.text,
      border: `1px solid ${c.border}`
    }}>
      {label}
    </span>
  )
}

// ─── Ammo Card ────────────────────────────────────────────────
function AmmoCard({ ammo, isPro, onBuy }) {
  const [expanded, setExpanded] = useState(false)

  const barrierColor = ammo.barrier_rating >= 4 ? 'green' : ammo.barrier_rating >= 3 ? 'gold' : 'red'
  const woundColor   = ammo.wound_channel.includes('Multi') ? 'purple'
    : ammo.wound_channel.includes('Frag') ? 'blue' : 'blue'

  return (
    <div
      style={{
        ...styles.card,
        ...(ammo.best_use ? styles.cardFeatured : {}),
      }}
    >
      {/* Featured Banner */}
      {ammo.best_use && (
        <div style={styles.featuredBanner}>
          {ammo.best_use}
        </div>
      )}

      {/* Twist Warning */}
      <TwistWarning warning={ammo.twist_warning} />

      {/* Top Row */}
      <div style={styles.cardTop}>
        <div style={styles.cardLeft}>
          <div style={styles.ammoName}>{ammo.name}</div>
          <div style={styles.ammoCal}>{ammo.calibers[0]} · {ammo.bullet_type}</div>
        </div>
        <div style={styles.grainBadge}>{ammo.grains}gr</div>
      </div>

      {/* Stats Row */}
      <div style={styles.statsGrid}>
        <StatCell label="MV (corrected)" value={`${ammo.corrected_mv || ammo.base_mv}`} unit="fps" />
        <StatCell label="BC (G1)"        value={ammo.bc_g1}                               unit=""    />
        <StatCell label="Ethical Range"  value={ammo.ethical_range_yds || '—'}            unit={ammo.ethical_range_yds ? 'yd' : ''} />
      </div>

      {/* Terminal Tags */}
      <div style={styles.tagRow}>
        <TermTag label={ammo.wound_channel}  color={woundColor}   />
        <TermTag label={`Barrier: ${ammo.barrier_rating}/5`} color={barrierColor} />
        {ammo.fbi_protocol && <TermTag label="FBI Tested" color="gold" />}
        {ammo.le_adopted   && <TermTag label="LE Adopted" color="green" />}
      </div>

      {/* Penetration Bar */}
      <div style={styles.penRow}>
        <span style={styles.penLabel}>Penetration</span>
        <PenetrationBar rating={ammo.penetration_rating} />
      </div>

      {/* Expand Toggle */}
      <button style={styles.expandBtn} onClick={() => setExpanded(e => !e)}>
        {expanded ? 'Less detail ▲' : 'Full terminal data ▼'}
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div style={styles.expanded}>
          <DetailRow label="Construction"      value={ammo.construction} />
          <DetailRow label="Expansion Reliable" value={ammo.expansion_reliable ? 'Yes' : 'No'} />
          <DetailRow label="Min Expansion Vel" value={ammo.min_expansion_vel ? `${ammo.min_expansion_vel} fps` : 'N/A'} />
          <DetailRow label="Weight Retention"  value={`${ammo.weight_retention_pct}%`} />
          <DetailRow label="Manufacturer"      value={ammo.manufacturer} />
          {ammo.notes && (
            <div style={styles.noteBox}>
              <div style={styles.noteLabel}>Notes</div>
              <div style={styles.noteText}>{ammo.notes}</div>
            </div>
          )}
        </div>
      )}

      {/* Buy Button */}
      <BuyButton ammo={ammo} isPro={isPro} onBuy={onBuy} />
    </div>
  )
}

// ─── Buy Button ───────────────────────────────────────────────
function BuyButton({ ammo, isPro, onBuy }) {
  // Phase 1: placeholder — will be replaced with live AvantLink data
  const hasLink = !!ammo.affiliate_url

  if (!hasLink) {
    return (
      <div style={styles.buyPlaceholder}>
        <span style={styles.buyPlaceholderText}>
          {ammo.msrp_per_rd ? `~$${ammo.msrp_per_rd.toFixed(2)}/rd` : 'Pricing TBD'}
        </span>
        <span style={styles.buyComingSoon}>Live pricing coming soon</span>
      </div>
    )
  }

  return (
    <a
      href={ammo.affiliate_url}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.buyBtn}
      onClick={() => onBuy?.(ammo)}
    >
      Buy Now — ${ammo.msrp_per_rd?.toFixed(2)}/rd →
    </a>
  )
}

// ─── Sub-components ───────────────────────────────────────────
function StatCell({ label, value, unit }) {
  return (
    <div style={styles.statCell}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statVal}>
        {value}
        {unit && <span style={styles.statUnit}>{unit}</span>}
      </div>
    </div>
  )
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailVal}>{value}</span>
    </div>
  )
}

// ─── No Profile State ─────────────────────────────────────────
function NoProfile() {
  return (
    <div style={styles.noProfile}>
      <div style={styles.npIcon}>◎</div>
      <div style={styles.npTitle}>No rifle profile selected</div>
      <div style={styles.npSub}>
        Go to the Rifles tab and add your first profile to see compatible ammunition
      </div>
    </div>
  )
}

// ─── Pro Gate Banner ──────────────────────────────────────────
function ProBanner() {
  return (
    <div style={styles.proBanner}>
      <div style={styles.proTitle}>Upgrade to Pro</div>
      <div style={styles.proSub}>
        Unlock full terminal performance data, real-time pricing, ethical range calculator, and unlimited rifle profiles.
      </div>
      <button style={styles.proBtn}>View Pro Plans — $34.99/yr →</button>
    </div>
  )
}

// ─── Main AmmoPage ────────────────────────────────────────────
export default function AmmoPage({ activeProfile }) {
  const { isPro } = useAuth()
  const [useCase, setUseCase] = useState('all')
  const [sortBy, setSortBy]   = useState('default')
  const [search, setSearch]   = useState('')

  // Get ammo filtered by rifle profile + use case
  const allAmmo = useMemo(() => {
    if (!activeProfile) return []
    return getAmmoForProfile(activeProfile, useCase)
  }, [activeProfile, useCase])

  // Apply search filter
  const filtered = useMemo(() => {
    if (!search.trim()) return allAmmo
    const q = search.toLowerCase()
    return allAmmo.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.manufacturer?.toLowerCase().includes(q) ||
      a.bullet_type?.toLowerCase().includes(q)
    )
  }, [allAmmo, search])

  // Apply sort
  const sorted = useMemo(() => {
    const arr = [...filtered]
    switch (sortBy) {
      case 'penetration': return arr.sort((a, b) => b.penetration_rating - a.penetration_rating)
      case 'mv':          return arr.sort((a, b) => b.base_mv - a.base_mv)
      case 'bc':          return arr.sort((a, b) => b.bc_g1 - a.bc_g1)
      case 'range':       return arr.sort((a, b) => (b.ethical_range_yds || 0) - (a.ethical_range_yds || 0))
      default:            return arr.sort((a, b) => (b.best_use ? 1 : 0) - (a.best_use ? 1 : 0))
    }
  }, [filtered, sortBy])

  // Free tier: show 4 cards max, blur the rest
  const FREE_LIMIT = 4
  const visible  = isPro ? sorted : sorted.slice(0, FREE_LIMIT)
  const blurred  = isPro ? [] : sorted.slice(FREE_LIMIT)

  if (!activeProfile) return <NoProfile />

  return (
    <div style={styles.container}>

      {/* Active Profile Pill */}
      <div style={styles.profilePill}>
        <span style={styles.pillDot} />
        <span style={styles.pillText}>
          {activeProfile.nickname} · {activeProfile.caliber} · {activeProfile.barrel_length}" · {activeProfile.twist_rate}
        </span>
      </div>

      {/* Search */}
      <div style={styles.searchWrap}>
        <input
          style={styles.searchInput}
          placeholder="Search rounds..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Use Case Filter */}
      <div style={styles.filterRow}>
        {USE_CASES.map(u => (
          <button
            key={u.id}
            style={{
              ...styles.filterChip,
              ...(useCase === u.id ? styles.filterChipActive : {})
            }}
            onClick={() => setUseCase(u.id)}
          >
            {u.label}
          </button>
        ))}
      </div>

      {/* Sort Row */}
      <div style={styles.sortRow}>
        <span style={styles.sortLabel}>Sort:</span>
        <select
          style={styles.sortSelect}
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          <option value="default">Featured first</option>
          <option value="penetration">Penetration</option>
          <option value="mv">Muzzle velocity</option>
          <option value="bc">Ballistic coeff.</option>
          <option value="range">Ethical range</option>
        </select>
      </div>

      {/* Results Count */}
      <div style={styles.resultCount}>
        {sorted.length} round{sorted.length !== 1 ? 's' : ''} for {activeProfile.caliber}
        {search ? ` matching "${search}"` : ''}
      </div>

      {/* Ammo Cards */}
      <div style={styles.cardList}>
        {visible.map(ammo => (
          <AmmoCard
            key={ammo.id}
            ammo={ammo}
            isPro={isPro}
          />
        ))}

        {/* Blurred locked cards for free tier */}
        {blurred.length > 0 && (
          <div style={styles.lockedWrap}>
            <div style={styles.lockedOverlay}>
              <div style={styles.lockedIcon}>🔒</div>
              <div style={styles.lockedTitle}>{blurred.length} more rounds available</div>
              <div style={styles.lockedSub}>Upgrade to Pro to see all compatible ammunition with full terminal data</div>
              <button style={styles.lockedBtn}>Upgrade to Pro — $34.99/yr</button>
            </div>
            <div style={{ filter: 'blur(4px)', pointerEvents: 'none' }}>
              {blurred.slice(0, 2).map(ammo => (
                <AmmoCard key={ammo.id} ammo={ammo} isPro={false} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pro banner at bottom for free users */}
      {!isPro && <ProBanner />}

    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────
const styles = {
  container:    { display: 'flex', flexDirection: 'column', gap: 0 },

  profilePill:  {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', background: 'var(--bg-secondary)',
    borderBottom: '1px solid var(--border)',
  },
  pillDot:  { width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 },
  pillText: { fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Courier New, monospace', letterSpacing: '0.04em' },

  searchWrap:   { padding: '12px 20px 0', },
  searchInput:  { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontSize: 13, width: '100%', outline: 'none' },

  filterRow:    { display: 'flex', gap: 8, padding: '12px 20px 0', overflowX: 'auto' },
  filterChip:   {
    padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--text-muted)', fontSize: 11,
    cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'Courier New, monospace',
    letterSpacing: '0.04em', transition: 'all 0.2s',
  },
  filterChipActive: { background: 'var(--accent)', color: '#0a0a0a', borderColor: 'var(--accent)', fontWeight: 700 },

  sortRow:      { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px 0' },
  sortLabel:    { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', textTransform: 'uppercase', letterSpacing: '0.08em' },
  sortSelect:   { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-secondary)', fontFamily: 'Courier New, monospace', fontSize: 11, padding: '5px 10px', flex: 1 },

  resultCount:  { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', padding: '8px 20px 0', letterSpacing: '0.04em' },

  cardList:     { display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 20px 20px' },

  card: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 10,
    transition: 'border-color 0.2s',
  },
  cardFeatured: { borderColor: 'rgba(200,169,110,0.35)', background: 'var(--bg-card)' },

  featuredBanner: {
    background: 'var(--accent-dim)', border: '1px solid var(--accent-border)',
    color: 'var(--accent-light)', fontSize: 10, fontFamily: 'Courier New, monospace',
    letterSpacing: '0.08em', padding: '4px 10px', borderRadius: 4,
    alignSelf: 'flex-start', textTransform: 'uppercase',
  },

  twistWarn: {
    display: 'flex', alignItems: 'flex-start', gap: 7,
    background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)',
    borderRadius: 6, padding: '7px 10px',
  },
  warnIcon: { fontSize: 12, color: 'var(--red)', flexShrink: 0 },
  warnText: { fontSize: 11, color: 'var(--red)', fontFamily: 'Courier New, monospace', lineHeight: 1.4 },

  cardTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  cardLeft: { flex: 1, marginRight: 10 },
  ammoName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 },
  ammoCal:  { fontSize: 10, color: 'var(--accent)', fontFamily: 'Courier New, monospace', marginTop: 3 },
  grainBadge: {
    background: 'var(--accent-dim)', border: '1px solid var(--accent-border)',
    color: 'var(--accent)', fontFamily: 'Courier New, monospace',
    fontSize: 11, padding: '3px 9px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0,
  },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 },
  statCell:  { background: 'var(--bg-tertiary)', borderRadius: 6, padding: '7px 8px' },
  statLabel: { fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace' },
  statVal:   { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', marginTop: 2 },
  statUnit:  { fontSize: 9, color: 'var(--text-muted)', marginLeft: 2 },

  tagRow:    { display: 'flex', flexWrap: 'wrap', gap: 6 },
  termTag:   { fontSize: 10, padding: '3px 8px', borderRadius: 4, fontFamily: 'Courier New, monospace' },

  penRow:    { display: 'flex', alignItems: 'center', gap: 10 },
  penLabel:  { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.06em' },
  barWrap:   { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  barTrack:  { flex: 1, height: 4, background: 'var(--bg-tertiary)', borderRadius: 2, overflow: 'hidden' },
  barFill:   { height: '100%', borderRadius: 2, transition: 'width 0.6s' },
  barLabel:  { fontSize: 11, fontFamily: 'Courier New, monospace', fontWeight: 600, minWidth: 28 },

  expandBtn: {
    background: 'none', border: 'none', color: 'var(--text-muted)',
    fontSize: 11, fontFamily: 'Courier New, monospace', cursor: 'pointer',
    textAlign: 'left', padding: 0, letterSpacing: '0.04em',
  },

  expanded:  { display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 10 },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  detailLabel: { fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace' },
  detailVal:   { fontSize: 11, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', fontWeight: 600 },
  noteBox:   { background: 'var(--bg-tertiary)', borderRadius: 6, padding: '8px 10px', marginTop: 4 },
  noteLabel: { fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace', marginBottom: 4 },
  noteText:  { fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 },

  buyBtn: {
    display: 'block', textAlign: 'center', textDecoration: 'none',
    background: 'var(--accent)', color: '#0a0a0a',
    borderRadius: 8, padding: '11px 16px', fontSize: 13, fontWeight: 700,
    fontFamily: 'Georgia, serif', letterSpacing: '0.03em',
  },
  buyPlaceholder: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'var(--bg-tertiary)', borderRadius: 8, padding: '10px 14px',
    border: '1px dashed var(--border)',
  },
  buyPlaceholderText: { fontSize: 13, color: 'var(--accent)', fontFamily: 'Courier New, monospace', fontWeight: 600 },
  buyComingSoon:      { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace' },

  noProfile: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 30px', gap: 14 },
  npIcon:    { fontSize: 40, color: 'var(--accent)' },
  npTitle:   { fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' },
  npSub:     { fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', textAlign: 'center', lineHeight: 1.6, maxWidth: 280 },

  lockedWrap:    { position: 'relative' },
  lockedOverlay: {
    position: 'absolute', inset: 0, zIndex: 10,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(10,10,10,0.85)', borderRadius: 12, padding: 24, gap: 10, textAlign: 'center',
  },
  lockedIcon:    { fontSize: 28 },
  lockedTitle:   { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  lockedSub:     { fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', lineHeight: 1.5, maxWidth: 260 },
  lockedBtn:     {
    background: 'var(--accent)', color: '#0a0a0a', border: 'none',
    borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4,
  },

  proBanner: {
    margin: '0 20px 24px', background: 'linear-gradient(135deg, rgba(200,169,110,0.08), rgba(200,169,110,0.03))',
    border: '1px solid var(--accent-border)', borderRadius: 12,
    padding: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8,
  },
  proTitle:  { fontSize: 15, fontWeight: 700, color: 'var(--accent-light)' },
  proSub:    { fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', lineHeight: 1.5 },
  proBtn:    {
    background: 'var(--accent)', color: '#0a0a0a', border: 'none',
    borderRadius: 8, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4,
  },
}
